import { AxiosResponse } from "axios";
import { axiosRetryGet } from "../../../utilities/AxiosRetryUtil";
import authModule from "../../../utilities/AuthModule";

// Runaway guard only: with $top=5000 on the first url this allows 500k rows.
export const MAX_SHAREPOINT_ITEM_PAGES = 100;

// nometadata/minimalmetadata answers "odata.nextLink", OData v4 "@odata.nextLink", verbose "d.__next".
export const getODataNextLink = (data: any): string | undefined =>
  data?.["odata.nextLink"] || data?.["@odata.nextLink"] || data?.d?.__next || undefined;

/**
 * Walks SharePoint REST server-driven paging from firstPageUrl, handing each page's rows to
 * onPage as it arrives. SharePoint's /items default page is 100 rows, so callers should put
 * $top=5000 (the maximum) on firstPageUrl. The next link is requested verbatim: it already
 * carries $select/$expand/$top and the keyset $skiptoken (p_ID), so pages are sequential and
 * threshold-safe as long as the first url adds no $orderby/$filter on a non-indexed column.
 * A failing page (after axiosRetryGet's own retries) rejects; earlier pages were delivered.
 */
export const forEachSharePointItemPage = async (firstPageUrl: string, onPage: (items: any[]) => void): Promise<void> => {
  const requested = new Set<string>();
  let nextLink: string | undefined = firstPageUrl;

  while (nextLink && !requested.has(nextLink) && requested.size < MAX_SHAREPOINT_ITEM_PAGES) {
    requested.add(nextLink);
    // Token per page: MSAL serves it from cache, and a long walk cannot outlive it.
    const requestConfig = {
      headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, Accept: "application/json;odata=nometadata" }
    };
    const response: AxiosResponse<any> = await axiosRetryGet(nextLink, requestConfig);
    const pageItems: any[] = response?.data?.value;
    if (Array.isArray(pageItems)) {
      const items = pageItems.filter(item => item);
      if (items.length > 0) {
        onPage(items);
      }
    }
    nextLink = getODataNextLink(response?.data);
  }
};
